import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { branchApi, customerApi, workflowApi } from "../../Apis/Api";
import LoadingComponent from "../../components/ui/LoadingComponent";
import jsQR from "jsqr";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import PlateNumberComponent from "./PlateNumberComponent";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import CustomerPhoneNumber from "./CustomerPhoneNumber";
import TicketComponent from "../../components/workflow/TicketComponent";
import ReceiptComponent from "../../components/workflow/ReceiptComponent";
import QRScanner from "../../components/QRScanner";

const colorOptions = [
  { value: "white", label: "White", hex: "#FFFFFF" },
  { value: "black", label: "Black", hex: "#000000" },
  { value: "silver", label: "Silver", hex: "#C0C0C0" },
  { value: "gray", label: "Gray", hex: "#808080" },
  { value: "blue", label: "Blue", hex: "#2196F3" },
  { value: "red", label: "Red", hex: "#F44336" },
  { value: "brown", label: "Brown", hex: "#795548" },
  { value: "green", label: "Green", hex: "#4CAF50" },
  { value: "yellow", label: "Yellow", hex: "#FFEB3B" },
  { value: "gold", label: "Gold", hex: "#FFD700" },
  { value: "beige", label: "Beige", hex: "#F5F5DC" },
  { value: "maroon", label: "Maroon", hex: "#800000" },
  { value: "orange", label: "Orange", hex: "#FF9800" },
  { value: "purple", label: "Purple", hex: "#9C27B0" },
  { value: "navy-blue", label: "Navy Blue", hex: "#000080" },
  { value: "dark-gray", label: "Dark Gray", hex: "#A9A9A9" },
  { value: "light-blue", label: "Light Blue", hex: "#ADD8E6" },
  { value: "teal", label: "Teal", hex: "#009688" },
];


// ===================== PAYMENT ICONS & COLORS =====================
const paymentMethodIcons = {
  "credit_card": "💳",
  "debit_card": "💳",
  "cash": "💰",
  "digital_wallet": "📱",
  "apple_pay": "🍎",
  "google_pay": "📱",
  "paypal": "🔵",
  "bank_transfer": "🏦",
  "card": "💳",
  "wallet": "👛",
  "visa": "💳",
  "mastercard": "💳",
  "amex": "💳",
  "mada": "💳",
  "sadad": "💳",
  "stc_pay": "📱"
};

const paymentStatusIcons = {
  "pending": "⏳",
  "paid": "✅",
  "completed": "✅",
  "success": "✅",
  "failed": "❌",
  "declined": "❌",
  "refunded": "↩️",
  "cancelled": "❌",
  "processing": "⚙️",
  "in_progress": "🔄",
  "partially_paid": "💱",
  "unpaid": "❌",
  "due": "⚠️",
  "overdue": "🔴"
};

// Icon colors based on status
const paymentStatusColors = {
  "pending": "#FFA500",
  "paid": "#28a745",
  "completed": "#28a745",
  "success": "#28a745",
  "failed": "#dc3545",
  "declined": "#dc3545",
  "refunded": "#6c757d",
  "cancelled": "#dc3545",
  "processing": "#17a2b8",
  "in_progress": "#17a2b8",
  "partially_paid": "#fd7e14",
  "unpaid": "#dc3545",
  "due": "#ffc107",
  "overdue": "#dc3545"
};

// Payment method colors
const paymentMethodColors = {
  "credit_card": "#2196F3",
  "debit_card": "#2196F3",
  "cash": "#4CAF50",
  "digital_wallet": "#9C27B0",
  "apple_pay": "#000000",
  "google_pay": "#4285F4",
  "paypal": "#003087",
  "bank_transfer": "#795548",
  "card": "#2196F3",
  "wallet": "#FF9800",
  "visa": "#1A1F71",
  "mastercard": "#EB001B",
  "amex": "#2E77BC",
  "mada": "#0A6E3A",
  "sadad": "#008CBA",
  "stc_pay": "#00A859"
};
// ===================== END PAYMENT ICONS & COLORS =====================

const SelectOptionItem = ({
  option,
  selectedSelectField,
  formData,
  setFormData,
  setShowSelectModal,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isSelected = formData[selectedSelectField] === option.value;
  const isOccupied = option.slot_type === "occupied";
  const isSlotField =
    selectedSelectField === "slot_number" ||
    selectedSelectField === "parking_slot_id";
  const isDisabled = isOccupied && isSlotField;

  // Payment field check
  const isPaymentMethod = selectedSelectField === "payment_method" || 
                         selectedSelectField.includes("payment_method");
  const isPaymentStatus = selectedSelectField === "payment_status" || 
                         selectedSelectField.includes("payment_status");
  const isPaymentField = isPaymentMethod || isPaymentStatus;
  
  // Get payment icon and color
  let paymentIcon = null;
  let paymentColor = null;
  
  if (isPaymentField) {
    const iconMap = isPaymentMethod ? paymentMethodIcons : paymentStatusIcons;
    const colorMap = isPaymentMethod ? paymentMethodColors : paymentStatusColors;
    paymentIcon = iconMap[option.value?.toLowerCase()] || 
                  (isPaymentMethod ? "💳" : "📄");
    paymentColor = colorMap[option.value?.toLowerCase()] || 
                   (isPaymentMethod ? "#2196F3" : "#6c757d");
  }

  const commonStyles = {
    padding: "var(--size-12)",
    borderRadius: "var(--rounded-8)",
    textAlign: "center",
    position: "relative",
    transition: "all 0.2s ease-in-out",
    userSelect: "none",
    backgroundColor: isPaymentField ? `${paymentColor || '#f8f9fa'}15` : "var(--white)",
    boxShadow: "var(--shadow-4)",
    border: isPaymentField ? `1px solid ${paymentColor || '#dee2e6'}30` : "1px solid var(--neutral-300)",
  };

  const disabledStyles = {
    ...commonStyles,
    cursor: "not-allowed",
    opacity: 0.6,
    filter: "grayscale(100%)",
    border: "1px solid var(--neutral-300)",
  };

  const enabledStyles = {
    ...commonStyles,
    cursor: "pointer",
    border: isSelected
      ? `2px solid ${isPaymentField ? paymentColor : "var(--primary-600)"}`
      : (isPaymentField ? `1px solid ${paymentColor}30` : "1px solid var(--neutral-300)"),
    backgroundColor: isSelected 
      ? (isPaymentField ? `${paymentColor}15` : "var(--neutral-100)") 
      : (isPaymentField ? `${paymentColor || '#f8f9fa'}10` : "var(--white)"),
    boxShadow: isSelected ? "var(--shadow-5)" : "var(--shadow-4)",
  };

  const handleMouseEnter = (e) => {
    if (!isSelected && !isDisabled) {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "var(--shadow-6)";
      e.currentTarget.style.backgroundColor = isPaymentField 
        ? `${paymentColor || '#f8f9fa'}20` 
        : "var(--neutral-100)";
    }
  };

  const handleMouseLeave = (e) => {
    if (!isSelected && !isDisabled) {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "var(--shadow-4)";
      e.currentTarget.style.backgroundColor = isPaymentField 
        ? `${paymentColor || '#f8f9fa'}10` 
        : "var(--white)";
    }
  };

  const handleClick = () => {
    console.log(
      `[DEBUG] SelectOptionItem Clicked - Field: ${selectedSelectField}, Value: ${option.label}`
    );
    if (!isDisabled) {
      setFormData((prev) => ({
        ...prev,
        [selectedSelectField]: option.value,
      }));
      setShowSelectModal(false);
    }
  };

  return (
    <div
      className={`select-option ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""} ${isPaymentField ? "payment-option" : ""}`}
      style={isDisabled ? disabledStyles : enabledStyles}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled}
    >
      {/* Status Indicator (Selected or Disabled) */}
      {(isSelected || isDisabled) && (
        <div
          style={{
            position: "absolute",
            top: "var(--size-8)",
            right: "var(--size-8)",
            width: "var(--size-16)",
            height: "var(--size-16)",
            borderRadius: "var(--rounded-48)",
            backgroundColor: isSelected 
              ? (isPaymentField ? paymentColor : "var(--primary-600)")
              : "var(--neutral-500)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="var(--size-8)"
            height="var(--size-8)"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--white)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {isSelected ? (
              <polyline points="20,6 9,17 4,12" />
            ) : (
              <path d="M5 12h14" />
            )}
          </svg>
        </div>
      )}

      {/* Icon/Image Section */}
      <div
        style={{
          height: "var(--size-48)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "var(--size-8)",
        }}
      >
        {/* Payment Field Icon */}
        {isPaymentField && paymentIcon && (
          <div
            style={{
              width: "var(--size-48)",
              height: "var(--size-48)",
              borderRadius: "var(--rounded-6)",
              backgroundColor: `${paymentColor}20`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "28px",
              color: paymentColor,
              boxShadow: `0 2px 8px ${paymentColor}30`
            }}
          >
            {paymentIcon}
          </div>
        )}
        
        {/* Vehicle Type/Make Image */}
        {(selectedSelectField === "vehicle_type" ||
          selectedSelectField === "vehicle_make") &&
          option.image && !isPaymentField && (
            <>
              {!imageLoaded && !imageError && (
                <div
                  className="shimmer-effect"
                  style={{
                    width: "var(--size-48)",
                    height: "var(--size-48)",
                    borderRadius: "var(--rounded-6)",
                    position: "absolute",
                    background:
                      "linear-gradient(90deg, var(--neutral-200) 25%, var(--neutral-300) 50%, var(--neutral-200) 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite",
                  }}
                />
              )}
              <img
                src={option.image}
                alt={option.label}
                style={{
                  width: "var(--size-48)",
                  height: "var(--size-48)",
                  objectFit: "contain",
                  borderRadius: "var(--rounded-6)",
                  backgroundColor: "var(--neutral-100)",
                  padding: "var(--size-4)",
                  opacity: imageLoaded ? 1 : 0,
                  transition: "opacity 0.3s ease-in-out",
                }}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </>
          )}

        {/* Fallback or Default Icon */}
        {((selectedSelectField === "vehicle_type" ||
          selectedSelectField === "vehicle_make") &&
          (!option.image || imageError) && !isPaymentField) ||
        (selectedSelectField === "vehicle_model" && !isPaymentField) ? (
          <svg
            style={{
              width: "var(--size-48)",
              height: "var(--size-48)",
              color: "var(--neutral-700)",
            }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="8" width="20" height="10" rx="2" ry="2" />
            <circle cx="7" cy="18" r="2" />
            <circle cx="17" cy="18" r="2" />
            <path d="M5 13L7 8" />
            <path d="M19 13L17 8" />
          </svg>
        ) : null}

        {/* Parking Slot Icon */}
        {isSlotField && !isPaymentField && (
          <svg
            style={{
              width: "var(--size-48)",
              height: "var(--size-48)",
              color: isDisabled ? "var(--neutral-500)" : "var(--neutral-700)",
            }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="6" width="18" height="12" rx="2" ry="2" />
            <path d="M7 10h10" />
            <path d="M7 14h4" />
            {isDisabled && (
              <g>
                <rect
                  x="8"
                  y="11"
                  width="8"
                  height="4"
                  rx="1"
                  fill="currentColor"
                  opacity="0.7"
                />
                <circle cx="10" cy="15.5" r="0.5" fill="currentColor" />
                <circle cx="14" cy="15.5" r="0.5" fill="currentColor" />
              </g>
            )}
          </svg>
        )}
      </div>

      {/* Label and Status */}
      <div
        style={{
          fontSize: "var(--font-sm)",
          fontWeight: "600",
          color: isPaymentField ? paymentColor : "var(--text-primary-light)",
          textTransform: "capitalize",
          fontFamily: "var(--default-font)",
        }}
      >
        {/* Payment icon before label */}
        {isPaymentField && paymentIcon && (
          <span style={{ 
            marginRight: "6px",
            fontSize: "14px",
            verticalAlign: "middle"
          }}>
            {paymentIcon}
          </span>
        )}
        
        {option.label}
        
        {/* Payment status badge */}
        {isPaymentStatus && option.value && (
          <div
            style={{
              fontSize: "var(--font-xs)",
              fontWeight: "500",
              color: paymentColor,
              marginTop: "var(--size-4)",
              padding: "var(--size-4) var(--size-8)",
              borderRadius: "var(--rounded-8)",
              backgroundColor: `${paymentColor}15`,
              border: `1px solid ${paymentColor}30`,
              display: "inline-block",
            }}
          >
            {option.value.replace(/_/g, ' ').toUpperCase()}
          </div>
        )}
        
        {/* Slot Status */}
        {isSlotField && option.slot_type && !isPaymentField && (
          <div
            style={{
              fontSize: "var(--font-xs)",
              fontWeight: "500",
              color: isDisabled ? "var(--neutral-500)" : "var(--success-600)",
              marginTop: "var(--size-4)",
              padding: "var(--size-4) var(--size-8)",
              borderRadius: "var(--rounded-8)",
              backgroundColor: isDisabled
                ? "var(--neutral-100)"
                : "var(--success-100)",
              border: `1px solid ${
                isDisabled ? "var(--neutral-300)" : "var(--success-border)"
              }`,
              display: "inline-block",
            }}
          >
            {isDisabled ? "🚗 Occupied" : "🅿️ Available"}
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== PAYMENT FIELD COMPONENT =====================
const PaymentField = ({ 
  field, 
  fieldValue, 
  onChange, 
  isReadOnly,
  setShowSelectModal,
  setSelectedSelectField 
}) => {
  const isPaymentMethod = field.name === "payment_method" || field.name.includes("payment_method");
  const isPaymentStatus = field.name === "payment_status" || field.name.includes("payment_status");
  
  if (!isPaymentMethod && !isPaymentStatus) return null;
  
  const iconMap = isPaymentMethod ? paymentMethodIcons : paymentStatusIcons;
  const colorMap = isPaymentMethod ? paymentMethodColors : paymentStatusColors;
  
  const icon = iconMap[fieldValue?.toLowerCase()] || (isPaymentMethod ? "💳" : "📄");
  const color = colorMap[fieldValue?.toLowerCase()] || (isPaymentMethod ? "#2196F3" : "#6c757d");
  const label = fieldValue ? fieldValue.replace(/_/g, ' ').toUpperCase() : `Select ${field.label}`;

  return (
    <div className="payment-field-wrapper">
      <div
        className="form-control payment-field-display"
        onClick={() => {
          if (!isReadOnly) {
            setSelectedSelectField(field.name);
            setShowSelectModal(true);
          }
        }}
        style={{
          backgroundColor: "var(--white)",
          border: `2px solid ${color}40`,
          height: "48px",
          padding: "8px 16px",
          borderRadius: "10px",
          cursor: isReadOnly ? "not-allowed" : "pointer",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          transition: "all 0.2s ease",
          opacity: isReadOnly ? 0.7 : 1,
          position: "relative",
          overflow: "hidden"
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = `0 4px 12px ${color}30`;
          }
        }}
        onMouseLeave={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
          }
        }}
      >
        {/* Icon with background */}
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            backgroundColor: `${color}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            color: color,
            flexShrink: 0
          }}
        >
          {icon}
        </div>
        
        {/* Text content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontSize: "14px", 
            fontWeight: "600",
            color: "var(--text-primary-light)",
            marginBottom: "2px",
            textTransform: "capitalize"
          }}>
            {field.label}
          </div>
          <div style={{ 
            fontSize: "15px", 
            fontWeight: "500",
            color: color,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
            {fieldValue ? (
              <>
                <span style={{ marginRight: "6px", fontSize: "16px" }}>{icon}</span>
                {label}
              </>
            ) : (
              <span style={{ color: "var(--neutral-500)" }}>Select an option</span>
            )}
          </div>
        </div>
        
        {/* Arrow indicator (if not read-only) */}
        {!isReadOnly && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="2"
            style={{ flexShrink: 0 }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
        
        {/* Status indicator dot for payment status */}
        {isPaymentStatus && fieldValue && (
          <div
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: color,
              border: "2px solid var(--white)"
            }}
          />
        )}
      </div>
      
      {/* Hidden input for form submission */}
      <input
        type="hidden"
        name={field.name}
        value={fieldValue || ""}
      />
    </div>
  );
};
// ===================== END PAYMENT FIELD COMPONENT =====================

const WorkflowRunningLayer = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const instanceId = state?.instanceId || null;
  const workflowId = state?.workflowId || null;
  const [showQRScanner, setShowQRScanner] = useState(false);
const [scanningField, setScanningField] = useState(null); // kon sa field scan kar rahe hain
const [fetchingCustomerData, setFetchingCustomerData] = useState(false);

  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const [currentTime, setCurrentTime] = useState(getCurrentDateTime());
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [selectedSelectField, setSelectedSelectField] = useState(null);

  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [stepDetails, setStepDetails] = useState(null);
  const [formData, setFormData] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});
  const [stepCount, setStepCount] = useState(null);
  const [showColorModal, setShowColorModal] = useState(false);
  const [selectedColorField, setSelectedColorField] = useState(null);
  const [currentStepNumber, setCurrentStepNumber] = useState(1);
  const [fieldOptions, setFieldOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const [workflowLoading, setWorkflowLoading] = useState(false);

  const [imageUploading, setImageUploading] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  let previousValidValue = useRef(null);

  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImageField, setEditingImageField] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [zoom, setZoom] = useState(1);
  const imgRef = useRef(null);
  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "onChange",
  });

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes shimmerAnimation {
        0% {
          background-position: -468px 0;
        }
        100% {
          background-position: 468px 0;
        }
      }
      
      .shimmer-effect {
        animation-duration: 1.5s;
        animation-fill-mode: forwards;
        animation-iteration-count: infinite;
        animation-name: shimmerAnimation;
        animation-timing-function: linear;
        background: #f6f7f8;
        background: linear-gradient(to right, #f6f7f8 0%, #edeef1 20%, #f6f7f8 40%, #f6f7f8 100%);
        background-size: 800px 104px;
        position: relative;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getCurrentDateTime());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!showSelectModal) {
      setSearchTerm("");
    }
  }, [showSelectModal]);

  useEffect(() => {
    const fields = getFormFields();
    const datetimeFields = fields.filter((field) => field?.type === "datetime");
    if (datetimeFields.length > 0) {
      setFormData((prev) => {
        const updatedFormData = { ...prev };
        let needsUpdate = false;
        datetimeFields.forEach((field) => {
          if (updatedFormData[field?.name] !== currentTime) {
            updatedFormData[field?.name] = currentTime;
            needsUpdate = true;
          }
        });
        return needsUpdate ? updatedFormData : prev;
      });
    }
  }, [currentTime, stepDetails?.data?.step_id]);

  const initializeFormData = (fields) => {
    const initialData = {};
    fields?.forEach((field) => {
      if (field?.name === "ticket_number" && field?.ticket_number) {
        initialData[field?.name] = String(field.ticket_number);
      } else if (
        field?.name === "tax_deduction" &&
        field?.default &&
        typeof field.default === "object"
      ) {
        initialData[field?.name] = field.default;
      } else if (field?.name === "tax_deduction") {
        initialData[field?.name] = 0;
      } else if (field?.default !== null && field?.default !== undefined) {
        initialData[field?.name] = field.default;
      } else if (field?.type === "datetime") {
        initialData[field?.name] = currentTime;
      } else if (field?.type === "qr_code" && field?.name === "qr_code") {
        const ticketField = fields.find((f) => f.name === "ticket_number");
        initialData[field?.name] = ticketField?.ticket_number || "";
      } else if (field?.type === "tel") {
        initialData[field?.name] = "";
        // initialData[`${field?.name}_country_code`] = "+1";
      } else if (
        field?.type === "select" ||
        field?.type === "file" ||
        field?.type === "image" ||
        field?.type === "scan_qr"
      ) {
        // ✅ Skip optional empty fields - don't initialize with ""
      } else {
        initialData[field?.name] = "";
      }
    });
    console.log("Initialized FormData:", initialData);
    return initialData;
  };

  useEffect(() => {
    if (!instanceId || !workflowId) {
      console.log("No instanceId or workflowId, showing workflow list");
      navigate("/home");
      return;
    }
    console.log("Received instanceId:", instanceId, "workflowId:", workflowId);
    resumeWorkflow(instanceId, workflowId);
  }, [instanceId, workflowId]);

  const resumeWorkflow = async (instanceId, workflowId, retries = 3) => {
    setWorkflowLoading(true);
    setFieldOptions({});
    setFormData({});
    setImagePreviews({});

    try {
      const stepResponse = await workflowApi.getWorkflowStep(instanceId);
      console.log(
        "Step Details Response:",
        JSON.stringify(stepResponse, null, 2)
      );

      const responseData = stepResponse?.data || stepResponse;
      if (!responseData) {
        throw new Error("Invalid step response: No response data received.");
      }

      const stepId =
        responseData?.step_id ||
        responseData?.id ||
        responseData?.stepId ||
        responseData?.step_code ||
        stepResponse?.step_id ||
        stepResponse?.id ||
        stepResponse?.step_code;

      if (!stepId) {
        throw new Error("Invalid step response: Missing step_id.");
      }

      const isAssigned = responseData?.isAssigned || stepResponse?.isAssigned;

      if (isAssigned === "N") {
        console.log("Workflow not assigned to current user, ending workflow.");
        toast.info("This workflow step is assigned to another user.");
        setSelectedWorkflow(null);
        setStepDetails(null);
        setWorkflowStatus(null);
        setFormData({});
        setImagePreviews({});
        setImageUploading({});
        navigate("/home");
        setWorkflowLoading(false);
        return;
      }

      let locationId =
        state?.locationId ||
        responseData?.location_id ||
        responseData?.data?.location_id ||
        null;
      let workflowName =
        responseData?.workflow_name || state?.workflowName || null;
      let workflowDetails = null;

      if (!locationId || !workflowName) {
        console.warn(
          "Missing location_id or workflow_name, fetching workflow details..."
        );
        workflowDetails = await workflowApi.getWorkflowDetails(workflowId);
        console.log(
          "Workflow Details Response:",
          JSON.stringify(workflowDetails, null, 2)
        );

        locationId =
          locationId ||
          workflowDetails?.location_id ||
          workflowDetails?.data?.location_id ||
          null;
        workflowName =
          workflowName ||
          workflowDetails?.name ||
          workflowDetails?.data?.name ||
          "Unknown Workflow";
      }

      const workflow = {
        workflow_id: workflowId,
        name: workflowName,
        location_id: locationId,
        location_name:
          responseData?.location_name ||
          workflowDetails?.location_name ||
          workflowDetails?.data?.location_name ||
          null,
      };

      console.log(
        "Workflow Name from stepResponse:",
        responseData?.workflow_name
      );
      console.log("Workflow Name from state:", state?.workflowName);
      console.log("Final Workflow Name:", workflowName);

      setSelectedWorkflow(workflow);
      setSelectedLocationId(locationId);

      const totalStepCount = responseData?.step_count || state?.stepCount || 1;
      let currentStepNumberCalc = 1;

      if (responseData?.step_history && responseData?.current_step) {
        const currentStepIndex = responseData.step_history.findIndex(
          (step) => step.step_label === responseData.current_step
        );
        currentStepNumberCalc =
          currentStepIndex >= 0
            ? currentStepIndex + 1
            : responseData.step_history.length + 1;
      } else if (state?.stepCount) {
        currentStepNumberCalc = state.stepCount;
      }

      if (responseData?.status === "completed") {
        const nextStepResponse = await workflowApi.getWorkflowStep(instanceId);
        const nextResponseData = nextStepResponse?.data || nextStepResponse;
        const nextStepId =
          nextResponseData?.step_id ||
          nextResponseData?.id ||
          nextResponseData?.step_code;

        if (!nextStepId) {
          toast.info("Workflow is complete. No further steps available.");
          navigate("/home");
          setWorkflowLoading(false);
          return;
        }

        if (
          nextResponseData?.isAssigned === "N" ||
          nextStepResponse?.isAssigned === "N"
        ) {
          console.log(
            "Next step not assigned to current user, ending workflow."
          );
          toast.info("This workflow step is assigned to another user.");
          setSelectedWorkflow(null);
          setStepDetails(null);
          setWorkflowStatus(null);
          setFormData({});
          setImagePreviews({});
          setImageUploading({});
          navigate("/home");
          setWorkflowLoading(false);
          return;
        }

        setStepDetails({
          ...nextStepResponse,
          data: {
            ...nextResponseData,
            step_id: nextStepId,
            instance_id: instanceId,
          },
        });
        setStepCount(nextStepResponse?.step_count || totalStepCount);
        setCurrentStepNumber(currentStepNumberCalc + 1);
      } else {
        setStepDetails({
          ...stepResponse,
          data: {
            ...responseData,
            step_id: stepId,
            instance_id: instanceId,
          },
        });
        setStepCount(totalStepCount);
        setCurrentStepNumber(currentStepNumberCalc);
      }

      setWorkflowStatus({ instance_id: instanceId });

      let fieldsToUse = [];
      if (responseData?.fields?.length > 0) {
        fieldsToUse = responseData.fields;
      } else if (stepResponse?.fields?.length > 0) {
        fieldsToUse = stepResponse.fields;
      } else if (responseData?.form_fields?.length > 0) {
        fieldsToUse = responseData.form_fields;
      } else {
        console.warn("No fields provided by API.");
        toast.error(
          "No fields available for this step. Showing workflow list."
        );
        navigate("/home");
        setWorkflowLoading(false);
        return;
      }

      const initialFormData = initializeFormData(fieldsToUse);
      setFormData(initialFormData);
      setShowForm(true);

      const fieldsWithDependency = detectDependentFields(fieldsToUse);
      fieldsWithDependency?.forEach((field) => {
        if (field.type === "select") {
          if (
            field.options?.length > 0 &&
            !field.options_source &&
            !field.data_source
          ) {
            fetchFieldOptions(field);
          } else if (field.options_source || field.data_source) {
            if (!field.dependent_field) {
              if (field.options_source?.includes("get-slots-by-location-id")) {
                fetchFieldOptions(field, locationId);
              } else {
                fetchFieldOptions(field);
              }
            }
          }
        }
      });
    } catch (err) {
      console.error("Resume Workflow Error:", {
        message: err.message,
        stack: err.stack,
        data: err.response?.data,
      });
      if (retries > 0) {
        console.log(`Retrying... (${retries} attempts left)`);
        setTimeout(
          () => resumeWorkflow(instanceId, workflowId, retries - 1),
          2000
        );
        return;
      }
      toast.error(
        err.message || "Failed to resume workflow. Showing workflow list."
      );
      navigate("/home");
    } finally {
      setWorkflowLoading(false);
    }
  };

  const detectDependentFields = (fields) => {
    return fields.map((field) => {
      if (field?.options_source?.includes("${branch_id}")) {
        return { ...field, dependent_field: "branch_name" };
      }
      if (field?.options_source?.includes("${location_id}")) {
        return { ...field, dependent_field: null };
      }
      if (field?.name === "vehicle_model") {
        return { ...field, dependent_field: "vehicle_make" };
      }
      if (field?.name === "slot") {
        return { ...field, dependent_field: "location" };
      }
      return field;
    });
  };

  const fetchFieldOptions = async (field, dependentValue = null) => {
    const dataSource = field.options_source || field.data_source;
    if (!dataSource && field.options?.length > 0) {
      setFieldOptions((prev) => ({
        ...prev,
        [field.name]: field.options.map((option) => ({
          value: option[field.options_value_field || "value"] || option,
          label: option[field.options_label_field || "label"] || option,
          image: option.type_image || option.make_image || null,
          slot_type: option.slot_type || null, // Add slot_type support
        })),
      }));
      return;
    }

    if (!dataSource) return;

    if (fieldOptions[field.name] && !dependentValue) return;

    if (
      dataSource.includes("get-slots-by-location-id") &&
      !dependentValue &&
      !selectedLocationId
    ) {
      setLoadingOptions((prev) => ({ ...prev, [field.name]: false }));
      return;
    }

    setLoadingOptions((prev) => ({ ...prev, [field.name]: true }));

    try {
      let url = dataSource;
      if (dependentValue) {
        url = url.replace(/\{makeId\}/g, dependentValue);
      }

      let response;
      if (
        url.includes("get-all-vehicle-makes") ||
        url.includes("get-all-vehicle-types") ||
        url.includes("get-vehicle-models-by-make")
      ) {
        response = await workflowApi.getFieldOptions(url);
      } else if (url.includes("get-client-branches")) {
        response = await branchApi.getClientBranches();
      } else if (url.includes("customer-profiles")) {
        response = await customerApi.getCustomerProfiles();
      } else if (url.includes("get-branch-locations")) {
        const params = { search: "", per_page: 100, sort_by: "location_name" };
        response = await workflowApi.getBranchLocations(dependentValue, params);
      } else if (url.includes("get-slots-by-location-id")) {
        const params = { location_id: dependentValue || selectedLocationId };
        response = await branchApi.getSlotsByLocationId(params);
      } else {
        response = await workflowApi.getFieldOptions(url);
      }

      // Handle different response structures
      let dataArray =
        response?.data?.data ||
        response?.data ||
        response?.branches ||
        response?.customer_profiles ||
        response?.countries ||
        response?.locations ||
        response?.branch_locations ||
        response?.slots ||
        response;

      // Special handling for vehicle types/makes/models
      if (dataArray && !Array.isArray(dataArray)) {
        if (dataArray.data && Array.isArray(dataArray.data)) {
          dataArray = dataArray.data;
        } else if (dataArray.vehicle_types) {
          dataArray = dataArray.vehicle_types;
        } else if (dataArray.vehicle_makes) {
          dataArray = dataArray.vehicle_makes;
        } else if (dataArray.vehicle_models) {
          dataArray = dataArray.vehicle_models;
        } else {
          // Find first array in object
          const arrayKeys = Object.keys(dataArray).filter((key) =>
            Array.isArray(dataArray[key])
          );
          if (arrayKeys.length > 0) {
            dataArray = dataArray[arrayKeys[0]];
          }
        }
      }

      if (!Array.isArray(dataArray)) {
        console.warn(`Response for ${field.name} is not an array:`, response);
        dataArray = [];
      }

      // Format options with images and slot_type - specific handling for different field types
       const formattedOptions = dataArray.map((item) => {
      let image = null;

      // Handle different image properties based on field type
      if (field.name === "vehicle_type") {
        image = item.type_image || null;
      } else if (field.name === "vehicle_make") {
        image = item.make_image || null;
      } else {
        image = item.image || null;
      }

      // For slots, use slot_number as label and slot_id as value
      let value, label;
      if (field.name === "slot_number" || field.name === "parking_slot_id") {
        value = item.slot_id || item.id || item.value || item;
        label = item.slot_number || item.name || item.label || item;
      } else {
        value =
          item[field?.options_value_field || "value"] || item.id || item;
        label =
          item[field?.options_label_field || "label"] || item.name || item;
      }

      // Add icons for payment fields
      let paymentIcon = null;
      let paymentColor = null;
      
      if (field.name === "payment_method" || field.name.includes("payment_method")) {
        paymentIcon = paymentMethodIcons[value?.toLowerCase()] || "💳";
        paymentColor = paymentMethodColors[value?.toLowerCase()] || "#2196F3";
      } else if (field.name === "payment_status" || field.name.includes("payment_status")) {
        paymentIcon = paymentStatusIcons[value?.toLowerCase()] || "📄";
        paymentColor = paymentStatusColors[value?.toLowerCase()] || "#6c757d";
      }

      return {
        value: value,
        label: label,
        image: image,
        slot_type: item.slot_type || null, // Include slot_type from API response
        slot_status: item.is_active
          ? item.slot_type === "open"
            ? "Available"
            : "Occupied"
          : "Inactive",
        payment_icon: paymentIcon, // Add payment icon
        payment_color: paymentColor // Add payment color
      };
    });

    setFieldOptions((prev) => ({
      ...prev,
      [field.name]: formattedOptions,
    }));
  } catch (error) {
    console.error(`Error fetching options for ${field.name}:`, error);
    toast.error(`Failed to load options for ${field.label}`);
  } finally {
    setLoadingOptions((prev) => ({ ...prev, [field.name]: false }));
  }
};

  useEffect(() => {
    const rawFields = getFormFields();
    const fieldsWithDependency = detectDependentFields(rawFields);

    fieldsWithDependency?.forEach((field) => {
      if (field.type === "select") {
        // Force fetch vehicle_make options
        if (field.name === "vehicle_make") {
          fetchFieldOptions(field);
        }
        // Force fetch vehicle_type options
        else if (field.name === "vehicle_type") {
          fetchFieldOptions(field);
        }
        // Handle dependent fields
        else if (field.dependent_field) {
          const depValue = formData[field.dependent_field];
          if (depValue) {
            fetchFieldOptions(field, depValue);
          }
        }
        // Handle independent fields
        else if (field.options_source || field.data_source) {
          if (field.options_source?.includes("get-slots-by-location-id")) {
            fetchFieldOptions(field, selectedLocationId);
          } else {
            fetchFieldOptions(field);
          }
        }
        // Handle static options
        else if (field.options?.length > 0) {
          fetchFieldOptions(field);
        }
      }
    });
  }, [
    stepDetails,
    selectedLocationId,
    formData.vehicle_make,
    formData.location,
  ]);

  const startCamera = async (fieldName) => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Camera access is not supported by this browser.");
      console.error("getUserMedia not supported");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      setShowScanner(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current
            .play()
            .then(() => {
              console.log("Video started playing");
              scanQRCode(fieldName);
            })
            .catch((err) => {
              console.error("Error playing video:", err);
            });
        }
      }, 100);
    } catch (err) {
      console.error("Error accessing camera:", err);
      let errorMessage =
        "Failed to access camera. Please allow camera permissions.";
      if (err.name === "NotAllowedError") {
        errorMessage =
          "Camera access denied. Please enable camera permissions in your browser settings.";
      } else if (err.name === "NotFoundError") {
        errorMessage = "No camera found on this device.";
      } else if (err.name === "SecurityError") {
        errorMessage =
          "Camera access requires a secure context (HTTPS or localhost).";
      }
      toast.error(errorMessage);
      setShowScanner(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowScanner(false);
  };

  const scanQRCode = (fieldName) => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const video = videoRef.current;
    const scan = () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        );
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });
        if (code) {
          setFormData((prev) => ({
            ...prev,
            [fieldName]: code.data,
          }));
          toast.success(`QR Code scanned: ${code.data}`);
          stopCamera();
        } else {
          requestAnimationFrame(scan);
        }
      } else {
        requestAnimationFrame(scan);
      }
    };
    requestAnimationFrame(scan);
  };

  const openImageEditor = (fieldName) => {
    if (!imagePreviews[fieldName]) {
      console.error(`No preview URL found for field: ${fieldName}`);
      toast.error(`Image preview not available for ${fieldName}`);
      return;
    }
    console.log(
      `Opening image editor for field: ${fieldName}, URL: ${imagePreviews[fieldName]}`
    );
    setEditingImageField(fieldName);
    setShowImageEditor(true);
    setCrop({ unit: "%", width: 50, height: 50, x: 25, y: 25 });
    setZoom(1);
  };

  const closeImageEditor = () => {
    setShowImageEditor(false);
    setEditingImageField(null);
    setCrop({ unit: "%", width: 50, height: 50, x: 25, y: 25 });
    setZoom(1);
  };

  const getCroppedImage = (image, crop, zoom) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas is empty");
            return;
          }
          const file = new File([blob], `cropped-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          const previewUrl = URL.createObjectURL(file);
          resolve({ file, previewUrl });
        },
        "image/jpeg",
        1
      );
    });
  };

  const handleSaveCroppedImage = async () => {
    if (!imgRef.current || !crop || !editingImageField) return;

    setImageUploading((prev) => ({ ...prev, [editingImageField]: true }));
    try {
      const { file, previewUrl } = await getCroppedImage(
        imgRef.current,
        crop,
        zoom
      );
      const uploadResponse = await workflowApi.uploadImage(file);
      console.log(
        `Upload Response for ${editingImageField}:`,
        JSON.stringify(uploadResponse, null, 2)
      );
      if (uploadResponse?.status === "success" && uploadResponse?.tiny_url) {
        setFormData((prev) => ({
          ...prev,
          [editingImageField]: uploadResponse.tiny_url,
        }));
        setImagePreviews((prev) => {
          const newPreviews = { ...prev, [editingImageField]: previewUrl };
          console.log(
            `Updated imagePreviews for ${editingImageField}: ${previewUrl}`
          );
          return newPreviews;
        });
        toast.success(
          `Cropped image uploaded successfully for ${editingImageField}`
        );
        closeImageEditor();
      } else {
        throw new Error(
          `Upload failed for ${editingImageField}: No tiny_url returned`
        );
      }
    } catch (error) {
      console.error(
        `Error uploading cropped image for ${editingImageField}:`,
        error
      );
      toast.error(`Failed to upload cropped image for ${editingImageField}`);
    } finally {
      setImageUploading((prev) => ({ ...prev, [editingImageField]: false }));
    }
  };

  const handleInputChange = async (e) => {
    const { name, value, type, files } = e.target;
    console.log(
      `[DEBUG] Input Change - Field: ${name}, Type: ${type}, Value:`,
      files ? files[0] : value
    );

    try {
      if (type === "file" || type === "image") {
        const file = files?.[0];
        if (file) {
          const maxSize = 10 * 1024 * 1024;
          if (file.size > maxSize) {
            toast.error(
              `File size too large for ${name}. Maximum 10MB allowed.`
            );
            return;
          }
          const previewUrl = URL.createObjectURL(file);
          setImagePreviews((prev) => ({ ...prev, [name]: previewUrl }));
          setImageUploading((prev) => ({ ...prev, [name]: true }));

          try {
            const uploadResponse = await workflowApi.uploadImage(file);
            if (
              uploadResponse?.status === "success" &&
              uploadResponse?.tiny_url
            ) {
              setFormData((prev) => ({
                ...prev,
                [name]: uploadResponse.tiny_url,
              }));
              toast.success(`Image uploaded successfully for ${name}`);
            } else {
              throw new Error(
                `Upload failed for ${name}: No tiny_url returned`
              );
            }
          } catch (uploadError) {
            console.error(`Upload error for ${name}:`, uploadError);
            setImagePreviews((prev) => {
              const newPreviews = { ...prev };
              delete newPreviews[name];
              return newPreviews;
            });
            setFormData((prev) => {
              const newData = { ...prev };
              delete newData[name]; // ✅ remove field instead of ""
              return newData;
            });
            toast.error(
              uploadError.message || `Failed to upload image for ${name}`
            );
            e.target.value = "";
          } finally {
            setImageUploading((prev) => ({ ...prev, [name]: false }));
          }
        } else {
          console.log(`File cleared for ${name}`);
          setImagePreviews((prev) => {
            const newPreviews = { ...prev };
            delete newPreviews[name];
            return newPreviews;
          });
          setFormData((prev) => {
            const newData = { ...prev };
            delete newData[name]; // ✅ remove field instead of ""
            return newData;
          });
          setImageUploading((prev) => ({ ...prev, [name]: false }));
        }
      } else if (type === "datetime-local") {
        let formattedValue = getCurrentDateTime();
        if (value) {
          const date = new Date(value);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          const seconds = String(date.getSeconds()).padStart(2, "0");
          formattedValue = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
        setFormData((prev) => ({ ...prev, [name]: formattedValue }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }

      // Dependencies check
      const rawFields = getFormFields();
      const fieldsWithDependency = detectDependentFields(rawFields);
      const dependentFields = fieldsWithDependency.filter(
        (field) => field.dependent_field === name
      );
      if (dependentFields.length > 0) {
        dependentFields.forEach((depField) => {
          setFormData((prev) => ({ ...prev, [depField.name]: "" }));
          setFieldOptions((prev) => ({ ...prev, [depField.name]: [] }));
          if (value) {
            if (depField.options_source?.includes("get-slots-by-location-id")) {
              const selectedOption = fieldOptions[name]?.find(
                (opt) => opt.value === value
              );
              const locationId = selectedOption?.value || selectedLocationId;
              fetchFieldOptions(depField, locationId);
            } else {
              fetchFieldOptions(depField, value);
            }
          }
        });
      }
    } catch (error) {
      console.error(`Input change error for ${name}:`, error);
      toast.error(`Error processing ${name}: ${error.message}`);
    }
  };

  const isAnyImageUploading = useMemo(() => {
  return Object.values(imageUploading).some(Boolean);
}, [imageUploading]);
  // WorkflowRunningLayer component ke andar yeh function add karo
  const cleanCustomerData = () => {
    console.log("🧹 Cleaning all customer data from form");

    const fieldsToClean = [
      "customer_name",
      "customer_id",
      "mobile_number",
      "email",
      "customer_email",
      "national_id",
      "vehicle_color",
      "vehicle_make",
      "vehicle_model",
      "vehicle_type",
      "make_name",
      "model_name",
      "type_name",
      "plate_number",
    ];

    setFormData((prev) => {
      const cleanedData = { ...prev };

      fieldsToClean.forEach((field) => {
        if (cleanedData[field] !== undefined) {
          console.log(`🧹 Cleaning field: ${field}`);
          cleanedData[field] = "";
        }
      });

      return cleanedData;
    });

    // React Hook Form values bhi clear karo
    setTimeout(() => {
      fieldsToClean.forEach((field) => {
        if (control._fields[field]) {
          setValue(field, "");
        }
      });
    }, 100);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("handleSubmit triggered");
    console.log("Step Details:", JSON.stringify(stepDetails, null, 2));
    console.log("Form Data:", JSON.stringify(formData, null, 2));

    const stepId =
      stepDetails?.data?.step_id ||
      stepDetails?.data?.id ||
      stepDetails?.step_id;
    const instanceId =
      stepDetails?.data?.instance_id || workflowStatus?.instance_id;

    if (!stepId || !instanceId) {
      console.error("Missing step_id or instance_id:", { stepId, instanceId });
      toast.error("Workflow configuration error. Please restart the workflow.");
      navigate("/home");
      return;
    }

    setWorkflowLoading(true);
    setShowForm(false);

    try {
      // const fields = getFormFields();
      // const submitData = {};
      // let validationFailed = false;

      // fields?.forEach((field) => {
      //   const value = formData[field?.name];

      //   // ✅ Required field validation
      //   if (field?.required === true) {
      //     if (value === undefined || value === null || value === "") {
      //       toast.error(`${field?.label || field?.name} is required`);
      //       validationFailed = true;
      //       return; // skip this field
      //     }
      //   }

       const fields = getFormFields();
    const submitData = {};
    let validationFailed = false;
    let firstMissingField = null; // ✅ Track first missing field

    // ✅ First pass: Check all required fields
    fields?.forEach((field) => {
      const value = formData[field?.name];

      // ✅ Required field validation - sirf check karo
      if (field?.required === true) {
        if (value === undefined || value === null || value === "") {
          if (!firstMissingField) {
            firstMissingField = field; // ✅ Sirf pehla missing field store karo
          }
          validationFailed = true;
          return; // skip this field
        }
      }
    });

    // ✅ Agar validation failed hai, sirf ek toast dikhao
    if (validationFailed && firstMissingField) {
      toast.error(`${firstMissingField?.label || firstMissingField?.name} is required`);
      setWorkflowLoading(false);
      setShowForm(true);
      return;
    }

    // ✅ Agar saare required fields bhare hue hain, to data process karo
    fields?.forEach((field) => {
      const value = formData[field?.name];

        // ✅ Handle special cases
        if (field?.name === "tax_deduction") {
          const taxDeduction = value;
          if (
            taxDeduction &&
            typeof taxDeduction === "object" &&
            taxDeduction.breakdown
          ) {
            submitData[field?.name] = taxDeduction;
          } else {
            submitData[field?.name] = 0;
          }
        } else if (field?.type === "datetime" && !value) {
          submitData[field?.name] = getCurrentDateTime();
        } else if (field?.name === "parked_time" && value) {
          const date = new Date(value.replace(" ", "T"));
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          const seconds = String(date.getSeconds()).padStart(2, "0");
          submitData[
            field?.name
          ] = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        } else {
          // ✅ Only add non-empty optional fields
          if (value !== undefined && value !== "") {
            submitData[field?.name] = value;
          }
        }

        console.log(
          `Submit Data entry: ${field?.name}=${JSON.stringify(
            submitData[field?.name]
          )}`
        );
      });

      // ✅ IMPORTANT: Plate number related fields manually add karo
      // Agar plate number field hai to related fields bhi include karo
      const plateNumberField = fields.find((f) => f.type === "plate_number");
      if (plateNumberField && formData[plateNumberField.name]) {
        // Plate country ID
        if (formData.plate_country_id) {
          submitData.country_id = formData.plate_country_id;
          console.log("Added plate_country_id:", formData.plate_country_id);
        }

        // Plate authority ID
        if (formData.plate_authority_id) {
          submitData.authority_id = formData.plate_authority_id;
          console.log("Added plate_authority_id:", formData.plate_authority_id);
        }

        // Auto-filled customer data fields bhi include karo
        const autoFillFields = [
          "customer_name",
          "customer_id",
          "customer_phone",
          "email",
          "national_id",
          "vehicle_make",
          "vehicle_model",
          "vehicle_type",
          "vehicle_color",
          "make_name",
          "model_name",
          "type_name",
          "registration_country",
          "authority_id",
          "country_id",
        ];

        autoFillFields.forEach((fieldName) => {
          if (formData[fieldName] && formData[fieldName] !== "") {
            submitData[fieldName] = formData[fieldName];
            console.log(
              `Added auto-filled field ${fieldName}:`,
              formData[fieldName]
            );
          }
        });
      }

      // 🚫 Stop submit if required fields missing
      if (validationFailed) {
        setWorkflowLoading(false);
        setShowForm(true);
        return;
      }

      console.log(
        "Submitting with Step ID:",
        stepId,
        "Instance ID:",
        instanceId,
        "Data:",
        JSON.stringify(submitData, null, 2)
      );

      const response = await workflowApi.submitReceiveRequest(
        stepId,
        instanceId,
        submitData
      );
      console.log("Submit Response:", JSON.stringify(response, null, 2));

      toast.success("Step submitted successfully!");
      if (isLastStep()) {
        console.log("Last step detected, calling completeWorkflow...");
        await handleCompleteWorkflow(instanceId);
        return;
      }
      if (response?.step_count) {
        setStepCount(response.step_count);
        setCurrentStepNumber((prev) => prev + 1);
      }

      console.log("Fetching next step for instance ID:", instanceId);
      const updatedStep = await workflowApi.getWorkflowStep(instanceId);
      console.log(
        "Updated Step Response:",
        JSON.stringify(updatedStep, null, 2)
      );

      const updatedStepId =
        updatedStep?.data?.step_id ||
        updatedStep?.data?.id ||
        updatedStep?.data?.stepId ||
        updatedStep?.step_id ||
        updatedStep?.id ||
        updatedStep?.step_code;

      if (!updatedStepId) {
        console.warn(
          "No step_id or step_code in updated step:",
          JSON.stringify(updatedStep, null, 2)
        );
        toast.error(
          "No step ID or code for next step. Workflow may be misconfigured."
        );
        navigate("/home");
        return;
      }

      if (
        updatedStep?.data?.isAssigned === "N" ||
        updatedStep?.isAssigned === "N"
      ) {
        console.log("Next step not assigned to current user, ending workflow.");
        toast.info("This workflow step is assigned to another user.");
        setSelectedWorkflow(null);
        setStepDetails(null);
        setWorkflowStatus(null);
        setFormData({});
        setImagePreviews({});
        setImageUploading({});
        navigate("/home");
        return;
      }

      setStepDetails({
        ...updatedStep,
        data: {
          ...updatedStep.data,
          step_id: updatedStepId,
          instance_id: instanceId,
        },
      });

      setStepCount(updatedStep?.step_count || response?.step_count);

      let fieldsToUse = [];
      if (updatedStep?.data?.fields?.length > 0) {
        fieldsToUse = updatedStep.data.fields;
      } else if (updatedStep?.fields?.length > 0) {
        fieldsToUse = updatedStep.fields;
      } else if (updatedStep?.data?.form_fields?.length > 0) {
        fieldsToUse = updatedStep.data.form_fields;
      } else {
        console.warn("No fields provided for next step.");
        toast.warn("No fields provided for next step.");
        fieldsToUse = [];
      }

      if (fieldsToUse.length > 0) {
        const initialFormData = initializeFormData(fieldsToUse);
        setFormData((prev) => ({ ...prev, ...initialFormData }));
        setImagePreviews((prev) => {
          const newPreviews = {};
          Object.entries(prev).forEach(([key, url]) => {
            if (url) {
              console.log(`Revoking URL for ${key}: ${url}`);
              URL.revokeObjectURL(url);
            }
          });
          return newPreviews;
        });
        setImageUploading({});
        setShowForm(true);
      } else {
        toast.error(
          "No valid fields for next step. Workflow may be misconfigured."
        );
        setShowForm(false);
        navigate("/home");
      }
    } catch (err) {
      console.error("Submit Error:", {
        message: err.message,
        response: err.response?.data,
        stack: err.stack,
      });
      toast.error(
        err.response?.data?.error || err.message || "Failed to submit step"
      );
      setShowForm(true);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleCompleteWorkflow = async (instanceId) => {
    if (!instanceId) {
      toast.error("No instance ID available to complete workflow.");
      return;
    }
    try {
      const response = await workflowApi.completeWorkflow(instanceId);
      console.log(
        "Complete Workflow Response:",
        JSON.stringify(response, null, 2)
      );
      if (response?.success || response?.message?.includes("completed")) {
        toast.success("Workflow completed successfully!");
        setWorkflowStatus(null);
        setShowForm(false);
        setSelectedWorkflow(null);
        setStepDetails(null);
        setWorkflowStatus(null);
        setFormData({});
        setImagePreviews((prev) => {
          const newPreviews = {};
          Object.entries(prev).forEach(([key, url]) => {
            if (url) {
              console.log(`Revoking URL for ${key}: ${url}`);
              URL.revokeObjectURL(url);
            }
          });
          return newPreviews;
        });
        setImageUploading({});
        navigate("/home");
      } else {
        toast.error("Failed to complete workflow. Please try again.");
      }
    } catch (err) {
      console.error("Complete Workflow Error:", {
        message: err.message,
        response: err.response?.data,
        stack: err.stack,
      });
      toast.error(
        err.response?.data?.error ||
          err.message ||
          "Failed to complete workflow"
      );
    }
  };

  const isLastStep = () => {
    if (stepCount && currentStepNumber) {
      return stepCount === 1 || currentStepNumber >= stepCount;
    }
    return (
      stepDetails?.data?.is_last_step ||
      stepDetails?.is_last_step ||
      stepDetails?.data?.status === "final" ||
      stepDetails?.data?.step_type === "final" ||
      stepDetails?.data?.is_final_step ||
      stepDetails?.data?.last_step ||
      stepDetails?.data?.next_step === null ||
      stepDetails?.data?.next_step_id === null ||
      stepDetails?.data?.step_name?.toLowerCase().includes("final") ||
      stepDetails?.data?.step_name?.toLowerCase().includes("complete") ||
      stepDetails?.data?.title?.toLowerCase().includes("final") ||
      stepDetails?.data?.title?.toLowerCase().includes("complete")
    );
  };

  const renderField = (field) => {
    const isReadOnly = field?.default !== null && field?.default !== undefined;
    const fieldValue =
      formData[field?.name] || (isReadOnly ? String(field.default) : "") || "";

    switch (field?.type) {
      case "plate_number":
        return (
          <PlateNumberComponent
            field={field}
            formData={formData}
            setFormData={setFormData}
            setValue={setValue}
            setFetchingCustomerData={setFetchingCustomerData}
          />
        );
      case "ticket_summary":
        const summaryData = field?.default || {};
     return  <TicketComponent data={summaryData} fieldName="ticket_summary"/>

        
      case "tel":
        return (
          <CustomerPhoneNumber
            name={field.name}
            control={control}
            errors={errors}
            defaultCountry="SA"
            label={field.label || "Phone Number"}
            labelName={false}
            placeholder="Enter phone number"
            onChange={(value) => {
              setFormData((prev) => ({
                ...prev,
                [field.name]: value || "",
              }));
              setValue(field.name, value || "");
            }}
            formData={formData}
            setFormData={setFormData}
            getFormFields={getFormFields}
            cleanCustomerData={cleanCustomerData}
            setFetchingCustomerData={setFetchingCustomerData}
          />
        );
      case "otp":
        return (
          <div>
            <input
              type="text"
              className="form-control"
              id={field?.name}
              name={field?.name}
              value={fieldValue}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d{0,6}$/.test(value)) {
                  setFormData((prev) => ({ ...prev, [field?.name]: value }));
                } else {
                  toast.error("OTP must be exactly 6 digits.");
                }
              }}
              maxLength={6}
              pattern="\d{6}"
              placeholder="Enter 6-digit OTP"
              required={field?.required}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              style={
                isReadOnly
                  ? { backgroundColor: "var(--white)", cursor: "not-allowed" }
                  : {}
              }
            />
            {fieldValue && !/^\d{6}$/.test(fieldValue) && (
              <small className="text-danger">
                Please enter a valid 6-digit OTP.
              </small>
            )}
          </div>
        );

      case "text":
      case "number":
        if (field?.name === "ticket_number") {
          return (
            <input
              type={field?.type}
              className="form-control"
              id={field?.name}
              name={field?.name}
              value={fieldValue || field?.ticket_number || ""}
              onChange={handleInputChange}
              required={field?.required}
              disabled={true}
              readOnly
              style={{ backgroundColor: "var(--white)", cursor: "not-allowed" }}
            />
          );
        }

        // Handle parking_duration specifically
        if (field?.name === "parking_duration") {
          return (
            <div>
              <input
                type="text" // Using text instead of number since value contains text
                className="form-control"
                id={field?.name}
                name={field?.name}
                value={fieldValue || field?.default || ""}
                onChange={handleInputChange}
                required={field?.required}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                style={
                  isReadOnly
                    ? { backgroundColor: "var(--white)", cursor: "not-allowed" }
                    : {}
                }
              />
            </div>
          );
        }

        // Default number field handling
        return (
          <div>
            <input
              type={field?.type}
              className="form-control"
              id={field?.name}
              name={field?.name}
              value={
                field?.name === "tax_deduction" &&
                field?.default &&
                typeof field.default === "object"
                  ? field.default.total
                  : fieldValue
              }
              onChange={handleInputChange}
              required={field?.required}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              style={
                isReadOnly
                  ? { backgroundColor: "var(--white)", cursor: "not-allowed" }
                  : { backgroundColor: "var(--white)" }
              }
            />
            {/* Render breakdown if it exists */}
            {field?.name === "tax_deduction" &&
              field?.default &&
              typeof field.default === "object" &&
              field.default.breakdown && (
                <div className="mt-2">
                  <h6 className="">Tax Breakdown</h6>
                  <ul className="list-group list-group-flush">
                    {field.default.breakdown.map((item, index) => (
                      <li
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center"
                        style={{ backgroundColor: "var(--white)" }}
                      >
                        <span style={{ color: `var(--text-primary-light)` }}>
                          {item.type}
                        </span>
                        <span style={{ color: `var(--text-primary-light)` }}>
                          {item.percent}%: {item.amount}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        );

      case "select":
    // Agar field payment related hai to custom component use karo
    if (field.name === "payment_method" || field.name === "payment_status" || 
        field.name.includes("payment_method") || field.name.includes("payment_status")) {
      return (
        <PaymentField
          field={field}
          fieldValue={fieldValue}
          onChange={handleInputChange}
          isReadOnly={isReadOnly}
          setShowSelectModal={setShowSelectModal}
          setSelectedSelectField={setSelectedSelectField}
        />
      );
    }
    
    // Baaki select fields ke liye original logic
    const options = fieldOptions[field?.name] || field?.options || [];
    const isLoadingThisField = loadingOptions[field?.name];
    const formattedOptions = options.map((option) => {
      if (typeof option === "string") {
        return { value: option, label: option, image: null };
      } else if (typeof option === "object" && option !== null) {
        return {
          value:
            option[field?.options_value_field || "value"] || option.value,
          label:
            option[field?.options_label_field || "label"] || option.label,
          image: option.type_image || option.image || null,
        };
      }
      return option;
    });

    const selectedOption = formattedOptions.find(
      (opt) => opt.value === fieldValue
    );

    return (
      <div className="position-relative">
        <div
          className="form-control d-flex align-items-center"
          onClick={() => {
            if (!isReadOnly && !isLoadingThisField) {
              setSelectedSelectField(field?.name);
              setShowSelectModal(true);
            }
          }}
          style={{
            cursor:
              isReadOnly || isLoadingThisField ? "not-allowed" : "pointer",
            backgroundColor: isReadOnly ? `var(--white)` : `var(--white)`,
            border: "1px solid var(--input-stroke)",
            height: "38px",
            padding: "6px 12px",
            opacity: isLoadingThisField ? 0.7 : 1,
          }}
        >
          {isLoadingThisField ? (
            <div className="d-flex align-items-center w-100 justify-content-center">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Loading...
            </div>
          ) : fieldValue && selectedOption ? (
            <>
              {selectedOption.image && (
                <img
                  src={selectedOption.image}
                  alt={selectedOption.label}
                  style={{
                    width: "24px",
                    height: "24px",
                    objectFit: "contain",
                    marginRight: "10px",
                    borderRadius: "4px",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              {selectedOption.label || "Select Option"}
            </>
          ) : (
            "Select Option"
          )}
        </div>

        {!isLoadingThisField && formattedOptions.length === 0 && (
          <div className="text-danger small mt-1">
            No options available for {field.label}
          </div>
        )}

        {field.dependent_field && !formData[field.dependent_field] && (
          <div className="text-info small mt-1">
            Please select {field.dependent_field.replace(/_/g, " ")} first
          </div>
        )}
      </div>
    );

      case "qr_code":
        const qrValue =
          formData[field?.name] || field?.ticket_number || "No data";
        return (
          <div className="qr-code-container d-flex justify-content-center">
            <div
              className="qr-code-display bg-white position-relative overflow-hidden"
              style={{
                width: "280px",
                background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                boxShadow:
                  "0 10px 30px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
                borderRadius: "12px",
              }}
            >
              <div
                className="text-center p-3 pb-2"
                style={{ borderBottom: "2px dashed #ddd" }}
              >
                <div className="mb-2">
                  <h5
                    className="mb-1 fw-bold"
                    style={{ letterSpacing: "2px", color: "#2c3e50" }}
                  >
                    QR CODE
                  </h5>
                  <div className="small text-neutral-900">
                    {field?.label || "Generated QR Code"}
                  </div>
                </div>
              </div>
              <div className="p-4 text-center">
                <div
                  className="qr-code-box mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "200px",
                    height: "200px",
                    backgroundColor: "#f8f9fa",
                    border: "2px solid #dee2e6",
                    borderRadius: "8px",
                    background: `url("https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                      qrValue
                    )}&bgcolor=ffffff&color=000000") center/cover`,
                  }}
                >
                  <div className="qr-fallback d-none">
                    <div
                      className="bg-dark"
                      style={{
                        width: "180px",
                        height: "180px",
                        background:
                          "repeating-linear-gradient(90deg, #000 0px, #000 8px, #fff 8px, #fff 16px), repeating-linear-gradient(0deg, #000 0px, #000 8px, #fff 8px, #fff 16px)",
                      }}
                    ></div>
                  </div>
                </div>
                <div className="small text-neutral-900">
                  Scan to get: Ticket Number
                </div>
              </div>
            </div>
          </div>
        );

      case "color":
        return (
          <div className="position-relative">
            <div
              className="form-control d-flex align-items-center"
              onClick={() => {
                if (!isReadOnly) {
                  setSelectedColorField(field?.name);
                  setShowColorModal(true);
                } else {
                  console.log("Field is read-only, no action taken");
                }
              }}
              style={{
                cursor: isReadOnly ? "not-allowed" : "pointer",
                backgroundColor: isReadOnly ? "var(--white)" : "var(--white)",
                border: "1px solid var(--input-stroke)",
                height: "38px",
                padding: "6px 12px",
              }}
            >
              {fieldValue ? (
                <>
                  <span
                    style={{
                      width: "20px",
                      height: "20px",
                      backgroundColor:
                        colorOptions.find((opt) => opt.value === fieldValue)
                          ?.hex || "#fff",
                      border: "1px solid var(--neutral-300)",
                      marginRight: "10px",
                      display: "inline-block",
                    }}
                  ></span>
                  {colorOptions.find((opt) => opt.value === fieldValue)
                    ?.label || "Select Color"}
                </>
              ) : (
                "Select Color"
              )}
            </div>
          </div>
        );

      // case "scan_qr":
      //   return (
      //     <div className="input-group">
      //       <input
      //         type="text"
      //         className="form-control"
      //         id={field?.name}
      //         name={field?.name}
      //         value={fieldValue}
      //         onChange={handleInputChange}
      //         required={field?.required}
      //         placeholder="Enter or scan QR code"
      //         readOnly={isReadOnly}
      //         disabled={isReadOnly}
      //         style={
      //           isReadOnly
      //             ? { backgroundColor: "var(--white)", cursor: "not-allowed" }
      //             : {}
      //         }
      //       />
      //       {!isReadOnly && (
      //         <button
      //           className="btn btn-outline-secondary"
      //           type="button"
      //           onClick={() => startCamera(field?.name)}
      //           disabled={showScanner}
      //           title="Scan QR Code"
      //         >
      //           📷
      //         </button>
      //       )}
      //       {showScanner && (
      //         <div
      //           className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      //           style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 9999 }}
      //         >
      //           <div
      //             className="bg-white p-4 rounded"
      //             style={{ maxWidth: "400px", width: "90%" }}
      //           >
      //             <h5 className="text-center mb-3">Scan QR Code</h5>
      //             <div className="text-center">
      //               <video
      //                 ref={videoRef}
      //                 autoPlay
      //                 playsInline
      //                 muted
      //                 style={{
      //                   width: "100%",
      //                   maxWidth: "300px",
      //                   height: "auto",
      //                   border: "2px solid #007bff",
      //                   borderRadius: "8px",
      //                 }}
      //               />
      //               <canvas ref={canvasRef} style={{ display: "none" }} />
      //               <div className="mt-3">
      //                 <button
      //                   type="button"
      //                   className="btn btn-danger me-2"
      //                   onClick={stopCamera}
      //                 >
      //                   <i className="bi bi-x-circle"></i> Stop Scanning
      //                 </button>
      //               </div>
      //             </div>
      //           </div>
      //         </div>
      //       )}
      //     </div>
      //   );

      case "scan_qr":
  return (
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        id={field?.name}
        name={field?.name}
        value={fieldValue}
        onChange={handleInputChange}
        required={field?.required}
        placeholder="Enter or scan QR code"
        readOnly={isReadOnly}
        disabled={isReadOnly}
        style={
          isReadOnly
            ? { backgroundColor: "var(--white)", cursor: "not-allowed" }
            : {}
        }
      />
      {!isReadOnly && (
        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={() => {
            setScanningField(field?.name);
            setShowQRScanner(true);
          }}
          disabled={showQRScanner}
          title="Scan QR Code"
        >
          Camera
        </button>
      )}

      {/* New ZXing QR Scanner */}
      <QRScanner
        show={showQRScanner}
        onClose={() => {
          setShowQRScanner(false);
          setScanningField(null);
        }}
        onScan={(result) => {
          setFormData((prev) => ({
            ...prev,
            [scanningField]: result,
          }));
          toast.success(`QR Code scanned: ${result}`);
        }}
      />
    </div>
  );
      case "receipt":
        const receiptData = field?.default || {};
        return  <ReceiptComponent data={receiptData} fieldName="receipt"/>

      case "datetime":
        const displayValue = fieldValue
          ? fieldValue.replace(" ", "T")
          : getCurrentDateTime().replace(" ", "T");
        return (
          <input
            type="datetime-local"
            className="form-control"
            id={field?.name}
            name={field?.name}
            value={displayValue}
            onChange={handleInputChange}
            required={field?.required}
            min={getCurrentDateTime().replace(" ", "T")}
            readOnly={true}
            disabled={true}
            style={{ backgroundColor: "var(--white)", cursor: "not-allowed" }}
          />
        );

      case "file":
      case "image":
        return (
          <div className="position-relative">
            <div className="position-relative">
              <input
                type="file"
                className={`form-control pe-5 ${
                  imageUploading[field?.name] ? "opacity-50" : ""
                }`}
                id={field?.name}
                name={field?.name}
                accept="image/*"
                onChange={handleInputChange}
                required={field?.required}
                disabled={imageUploading[field?.name] || isReadOnly}
                style={
                  isReadOnly
                    ? { backgroundColor: "var(--white)", cursor: "not-allowed" }
                    : {}
                }
              />
              {formData[field?.name] &&
                !imageUploading[field?.name] &&
                imagePreviews[field?.name] && (
                  <button
                    type="button"
                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                    onClick={() => openImageEditor(field?.name)}
                    style={{
                      width: "24px",
                      height: "24px",
                      zIndex: 2,
                    }}
                    title="View Image"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#6c757d"
                      viewBox="0 0 16 16"
                    >
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                    </svg>
                  </button>
                )}
              {imageUploading[field?.name] && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{ background: "rgba(0,0,0,0.1)" }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Uploading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <input
            type={field?.type}
            className="form-control"
            id={field?.name}
            name={field?.name}
            value={fieldValue}
            onChange={handleInputChange}
            required={field?.required}
            readOnly={isReadOnly}
            disabled={isReadOnly}
            style={
              isReadOnly
                ? { backgroundColor: "var(--white)", cursor: "not-allowed" }
                : { backgroundColor: "var(--white)" }
            }
          />
        );
    }
  };

  const getFormFields = () => {
    if (stepDetails?.data?.fields?.length > 0) {
      return stepDetails.data.fields;
    }
    if (stepDetails?.fields?.length > 0) {
      return stepDetails.fields;
    }
    if (stepDetails?.data?.form_fields?.length > 0) {
      return stepDetails.data.form_fields;
    }
    return [];
  };

  useEffect(() => {
    return () => {
      console.log("Cleaning up image previews on component unmount");
      Object.entries(imagePreviews).forEach(([key, url]) => {
        if (url) {
          console.log(`Revoking URL for ${key}: ${url}`);
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  return (
    <Container fluid className="py-3 px-4">
      {/* Loading Overlay */}
      {workflowLoading && (
        <div className="d-flex justify-content-center align-items-center">
          <div style={{ width: "400px" }}>
            <LoadingComponent />
          </div>
        </div>
      )}

      {/* Workflow Form */}
      {!workflowLoading && showForm && selectedWorkflow && (
        <Card className="mb-4">
          <Card.Header className=" d-flex flex-wrap justify-content-between align-items-center">
            <h5 className="mb-0">
              Attendant -{" "}
              {selectedWorkflow?.name ||
                `Workflow ${selectedWorkflow?.workflow_id}`}
            </h5>
            {stepCount && (
              <span className="badge bg-primary-600">
                Step {currentStepNumber} of {stepCount}:{" "}
                {stepDetails?.data?.step_label ||
                  stepDetails?.step_label ||
                  "Step"}
              </span>
            )}
          </Card.Header>
          <Card.Body className="position-relative">
            {workflowLoading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                style={{ backgroundColor: "rgba(255,255,255,0.8)", zIndex: 10 }}
              >
                <div className="text-center">
                  <div style={{ width: "400px" }}>
                    <LoadingComponent />
                  </div>
                  <p className="mt-2">Submitting step...</p>
                </div>
              </div>
            )}

            <Form onSubmit={handleSubmit}>
              <Row>
                {getFormFields()?.map((field) => (
                  <Col key={field?.name} xs={12} md={6} className="mb-3">
                    <Form.Label>
                      {field?.label}
                      {field?.required ? (
                        <span className="text-danger ms-1">*</span>
                      ) : (
                        <span
                          className=" ms-1"
                          style={{ fontSize: "0.85em", fontWeight: "normal" }}
                        >
                          (optional)
                        </span>
                      )}
                    </Form.Label>
                    {renderField(field)}
                  </Col>
                ))}
              </Row>

              <div className="mt-3">
                <button
                  type="submit"
                  variant={isLastStep() ? "success" : "primary"}
                  className={`w-100 btn btn-primary-600 ${isLastStep() ? "success" : ""}`}
                  disabled={workflowLoading ||isAnyImageUploading || fetchingCustomerData}
                >
                  {workflowLoading
                    ? isLastStep()
                      ? "Completing..."
                      : "Processing..."
                    : isLastStep()
                    ? "Completed"
                    : "Submit"}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={showColorModal && selectedColorField}
        onHide={() => setShowColorModal(false)}
        centered
        size="md"
        backdrop="static"
      >
        <Modal.Header
          className="border-0 pb-2"
          style={{ backgroundColor: "var(--white)" }}
        ></Modal.Header>

        <Modal.Body className="p-3" style={{ backgroundColor: "var(--white)" }}>
          <div
            className="d-grid gap-3"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {colorOptions.map((option) => (
              <div
                key={option.value}
                className={`color-option ${
                  formData[selectedColorField] === option.value
                    ? "selected"
                    : ""
                }`}
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  padding: "16px",
                  borderRadius: "12px",
                  border:
                    formData[selectedColorField] === option.value
                      ? "2px solid var(--primary-600)"
                      : "2px solid transparent",
                  backgroundColor:
                    formData[selectedColorField] === option.value
                      ? "var(--white)"
                      : "var(--white)",
                  transition: "all 0.2s ease-in-out",
                  textAlign: "center",
                  position: "relative",
                  boxShadow:
                    formData[selectedColorField] === option.value
                      ? "var(--shadow-5)"
                      : "var(--shadow-4)",
                }}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    [selectedColorField]: option.value,
                  }));
                  setShowColorModal(false);
                }}
                onMouseEnter={(e) => {
                  if (formData[selectedColorField] !== option.value) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "var(--shadow-6)";
                    e.currentTarget.style.backgroundColor =
                      "var(--neutral-100)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (formData[selectedColorField] !== option.value) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "var(--shadow-4)";
                    e.currentTarget.style.backgroundColor = "var(--white)";
                  }
                }}
              >
                {/* Selected indicator */}
                {formData[selectedColorField] === option.value && (
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      backgroundColor: "var(--primary-600)",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--white)"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  </div>
                )}

                {/* Color Circle */}
                <div
                  className="mx-auto mb-3"
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: option.hex,
                    borderRadius: "50%",
                    border: "3px solid var(--white)",
                    boxShadow:
                      "0 2px 8px rgba(0,0,0,0.15), inset 0 1px 2px rgba(255,255,255,0.3)",
                    position: "relative",
                  }}
                >
                  {/* Shine effect */}
                  <div
                    style={{
                      position: "absolute",
                      top: "3px",
                      left: "3px",
                      width: "15px",
                      height: "15px",
                      backgroundColor: "rgba(255,255,255,0.4)",
                      borderRadius: "50%",
                      filter: "blur(1px)",
                    }}
                  />
                </div>

                {/* Color Name */}
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "var(--text-secondary-light)",
                    marginBottom: "4px",
                    textTransform: "capitalize",
                  }}
                >
                  {option.label}
                </div>

                {/* Hex Code */}
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--neutral-500)",
                    fontFamily: "monospace",
                    backgroundColor: "var(--neutral-100)",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    display: "inline-block",
                  }}
                >
                  {option.hex}
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer
          className="border-0 pt-2"
          style={{ backgroundColor: "var(--white)" }}
        >
          <Button
            variant="outline-secondary"
            onClick={() => setShowColorModal(false)}
            style={{
              borderRadius: "8px",
              padding: "8px 20px",
              fontWeight: "500",
              marginTop: "50px",
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {showSelectModal && selectedSelectField && (
        <Modal
          show={showSelectModal && selectedSelectField}
          onHide={() => setShowSelectModal(false)}
          centered
          size={selectedSelectField === "parking_slot_id" ? "xl" : "md"}
          backdrop="static"
        >
          <Modal.Header
            className="border-0 pb-2"
            style={{ backgroundColor: "var(--white)" }}
          ></Modal.Header>

          <Modal.Body
            className="p-3"
            style={{
              backgroundColor: "var(--white)",
              minHeight:
                selectedSelectField === "parking_slot_id" ? "600px" : "auto",
            }}
          >
            {(selectedSelectField === "vehicle_type" ||
              selectedSelectField === "vehicle_make" ||
              selectedSelectField === "vehicle_model" ||
              selectedSelectField === "slot_number" ||
              selectedSelectField === "parking_slot_id") && (
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Search ${selectedSelectField.replace(
                    /_/g,
                    " "
                  )}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderRadius: "8px",
                    padding: "10px 15px",
                    fontSize: "14px",
                    backgroundColor: "var(--white)",
                  }}
                />
              </div>
            )}

            {/* Stats for parking slots */}
            {(selectedSelectField === "slot_number" ||
              selectedSelectField === "parking_slot_id") && (
              <div
                className="mb-4 p-3 rounded"
                style={{ backgroundColor: "var(--white)" }}
              >
                <div className="row text-center">
                  <div className="col-4">
                    <strong
                      style={{ color: "var(--success-600)", fontSize: "16px" }}
                    >
                      {
                        (fieldOptions[selectedSelectField] || [])
                          .filter((option) => option.slot_type === "open")
                          .filter((option) => {
                            if (!searchTerm) return true;
                            return option.label
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase());
                          }).length
                      }
                    </strong>
                    <div
                      style={{ fontSize: "12px", color: "var(--success-600)" }}
                    >
                      Available
                    </div>
                  </div>
                  <div className="col-4">
                    <strong
                      style={{ color: "var(--danger-600)", fontSize: "16px" }}
                    >
                      {
                        (fieldOptions[selectedSelectField] || [])
                          .filter((option) => option.slot_type === "occupied")
                          .filter((option) => {
                            if (!searchTerm) return true;
                            return option.label
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase());
                          }).length
                      }
                    </strong>
                    <div
                      style={{ fontSize: "12px", color: "var(--danger-600)" }}
                    >
                      Occupied
                    </div>
                  </div>
                  <div className="col-4">
                    <strong
                      style={{ color: "var(--primary-600)", fontSize: "16px" }}
                    >
                      {(fieldOptions[selectedSelectField] || []).length}
                    </strong>
                    <div
                      style={{ fontSize: "12px", color: "var(--primary-600)" }}
                    >
                      Total
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Parking lot style grid for parking_slot_id */}
            {selectedSelectField === "parking_slot_id" ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                  gap: "8px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  padding: "15px",
                  backgroundColor: "var(--white)",
                  borderRadius: "10px",
                }}
              >
                {(fieldOptions[selectedSelectField] || [])
                  .filter((option) => {
                    if (!searchTerm) return true;
                    return option.label
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase());
                  })
                  .map((option, index) => {
                    const isSelected =
                      formData[selectedSelectField] === option.value;
                    const isOccupied = option.slot_type === "occupied";

                    return (
                      <div
                        key={option.value || index}
                        className={`parking-slot ${
                          isSelected ? "selected" : ""
                        } ${isOccupied ? "occupied" : "available"}`}
                        style={{
                          width: "90px",
                          height: "120px",
                          border: isOccupied
                            ? "2px solid var(--danger-600)"
                            : isSelected
                            ? "3px solid var(--primary-600)"
                            : "2px dashed var(--success-600)",
                          borderRadius: "8px",
                          backgroundColor: isOccupied
                            ? "var(--neutral-800)"
                            : "var(--white)",
                          cursor: isOccupied ? "not-allowed" : "pointer",
                          position: "relative",
                          transition: "all 0.2s ease",
                          opacity: isOccupied ? 0.7 : 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px 4px",
                        }}
                        onClick={() => {
                          if (!isOccupied) {
                            setFormData((prev) => ({
                              ...prev,
                              [selectedSelectField]: option.value,
                            }));
                            setShowSelectModal(false);
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isOccupied && !isSelected) {
                            e.currentTarget.style.transform =
                              "translateY(-3px)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px rgba(40,167,69,0.3)";
                            e.currentTarget.style.borderColor =
                              "var(--success-600)";
                            e.currentTarget.style.borderStyle = "solid";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isOccupied && !isSelected) {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor =
                              "var(--success-600)";
                            e.currentTarget.style.borderStyle = "dashed";
                          }
                        }}
                      >
                        {/* Slot number at top */}
                        <div
                          style={{
                            backgroundColor: isOccupied
                              ? "var(--danger-600)"
                              : "var(--success-600)",
                            color: "var(--white)",
                            fontSize: "9px",
                            fontWeight: "600",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            marginBottom: "4px",
                            textAlign: "center",
                            minWidth: "60px",
                          }}
                        >
                          {option.label}
                        </div>

                        {/* Parking icon */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flex: "1",
                            marginBottom: "8px",
                          }}
                        >
                          <svg
                            width="30"
                            height="30"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            style={{
                              color: isOccupied
                                ? "var(--white)"
                                : "var(--primary-600)",
                            }}
                          >
                            <rect
                              x="3"
                              y="8"
                              width="18"
                              height="8"
                              rx="2"
                              ry="2"
                            ></rect>
                            <text
                              x="12"
                              y="14"
                              textAnchor="middle"
                              fontSize="8"
                              fill="var(--white)"
                              fontWeight="bold"
                            >
                              P
                            </text>
                          </svg>
                        </div>

                        {/* Car icon for occupied slots */}
                        {isOccupied && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginBottom: "8px",
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -10px)",
                            }}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="var(--danger-200)"
                            >
                              <rect
                                x="4"
                                y="10"
                                width="16"
                                height="6"
                                rx="1"
                              ></rect>
                              <circle cx="7" cy="16" r="1"></circle>
                              <circle cx="17" cy="16" r="1"></circle>
                              <path d="M6 10V8a2 2 0 012-2h8a2 2 0 012 2v2"></path>
                            </svg>
                          </div>
                        )}

                        {/* Status label */}
                        <div
                          style={{
                            fontSize: "8px",
                            fontWeight: "600",
                            color: isOccupied
                              ? "var(--white)"
                              : "var(--success-600)",
                            textAlign: "center",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {isOccupied ? "OCCUPIED" : "AVAILABLE"}
                        </div>

                        {/* Selected indicator */}
                        {isSelected && !isOccupied && (
                          <div
                            style={{
                              position: "absolute",
                              top: "-5px",
                              right: "-5px",
                              backgroundColor: "var(--primary-600)",
                              borderRadius: "50%",
                              width: "18px",
                              height: "18px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="var(--white)"
                              strokeWidth="3"
                            >
                              <polyline points="20,6 9,17 4,12"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : (
              // Regular grid for other fields
              <div
                className="d-grid gap-3 pt-2"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  maxHeight: "400px",
                  overflowY: "auto",
                }}
              >
                {(fieldOptions[selectedSelectField] || [])
                  .filter((option) => {
                    if (
                      selectedSelectField === "slot_number" &&
                      option.slot_type === "occupied"
                    ) {
                      return false;
                    }
                    if (!searchTerm) return true;
                    return option.label
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase());
                  })
                  .map((option, index) => (
                    <SelectOptionItem
                      key={option.value || index}
                      option={option}
                      selectedSelectField={selectedSelectField}
                      formData={formData}
                      setFormData={setFormData}
                      setShowSelectModal={setShowSelectModal}
                      searchTerm={searchTerm}
                    />
                  ))}
              </div>
            )}

            {/* No results message */}
            {(fieldOptions[selectedSelectField] || []).filter((option) => {
              if (
                selectedSelectField === "slot_number" &&
                option.slot_type === "occupied"
              ) {
                return false;
              }
              if (!searchTerm) return true;
              return option.label
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            }).length === 0 && (
              <div className="text-center py-4">
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>🚫</div>
                <p style={{ color: "var(--neutral-500)", fontSize: "14px" }}>
                  {selectedSelectField === "slot_number" ||
                  selectedSelectField === "parking_slot_id"
                    ? "No slots found"
                    : "No options found"}
                </p>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer
            className="border-0 pt-2"
            style={{ backgroundColor: "var(--white)" }}
          >
            <Button
              variant="outline-secondary"
              onClick={() => setShowSelectModal(false)}
              style={{
                borderRadius: "8px",
                padding: "8px 20px",
                fontWeight: "500",
                marginTop: "50px",
              }}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Image Editor Modal */}
      <Modal
        show={showImageEditor && editingImageField}
        onHide={closeImageEditor}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "var(--text-primary-light)" }}>
            Edit Image for {editingImageField}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {imagePreviews[editingImageField] ? (
            <div className="mb-3">
              <ReactCrop
                crop={crop}
                onChange={(newCrop) => setCrop(newCrop)}
                onComplete={(newCrop) => setCrop(newCrop)}
                aspect={undefined}
                minWidth={50}
                minHeight={50}
              >
                <img
                  ref={imgRef}
                  src={imagePreviews[editingImageField]}
                  alt={`Image for ${editingImageField}`}
                  style={{
                    transform: `scale(${zoom})`,
                    maxWidth: "100%",
                    height: "auto",
                  }}
                />
              </ReactCrop>
            </div>
          ) : (
            <div className="alert alert-warning">
              Image preview not available for {editingImageField}.
            </div>
          )}

          {imagePreviews[editingImageField] && (
            <div className="d-flex align-items-center gap-3">
              <Form.Label className="mb-0">Zoom:</Form.Label>
              <Form.Range
                min="0.5"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
              />
              <span style={{ color: "var(--text-primary-light)" }}>
                {(zoom * 100).toFixed(0)}%
              </span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeImageEditor}>
            Cancel
          </Button>
          {imagePreviews[editingImageField] && (
            <Button
              variant="primary-600"
              onClick={handleSaveCroppedImage}
              disabled={imageUploading[editingImageField]}
            >
              {imageUploading[editingImageField] ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WorkflowRunningLayer;
